import React, { useState } from 'react'
import { PIPELINE, CHROME } from '../../lib/viz'
import { statusLabel } from '../../lib/format'
import { number } from '../../lib/format'

/**
 * Where every order currently sits, as one part-to-whole bar.
 *
 * A stacked bar rather than a donut. The reader's question is "how many are
 * waiting to be packed" — a length is easy to compare against its neighbours,
 * where a donut asks them to judge angles, and a donut with seven slices asks
 * them to judge seven angles against a legend.
 *
 * Colour here is **ordinal**, not categorical: the statuses have a natural
 * order, so a single-hue ramp running light → dark says "further along" by
 * itself. The ramp was validated with `--ordinal` (monotone lightness, every
 * adjacent gap ≥ 0.06, light end 2.06:1 on the surface). Cancelled and refunded
 * sit outside the ramp and take status colours, because they are exceptions
 * rather than a later stage.
 */

// Pipeline order, then the two exception states. Not alphabetical — this is the
// sequence an order actually moves through.
const ORDER = ['pending', 'paid', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded']

const PipelineBar = ({ counts, onSelect = null }) => {
  const [hovered, setHovered] = useState(null)

  const rows = ORDER.map((status) => ({
    status,
    label: statusLabel(status),
    count: Number(counts?.get?.(status) ?? counts?.[status] ?? 0),
    color: PIPELINE[status],
  })).filter((r) => r.count > 0)

  const total = rows.reduce((sum, r) => sum + r.count, 0)

  if (total === 0) {
    return <div className="py-6 text-center text-sm text-ink-muted">No orders yet.</div>
  }

  return (
    <div>
      <div
        className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full"
        style={{ background: CHROME.grid }}
        role="img"
        aria-label={`Order pipeline: ${rows.map((r) => `${r.count} ${r.label}`).join(', ')}`}
      >
        {/* gap-0.5 is the 2px surface spacer between segments. Without it two
            adjacent ramp steps touch and read as one longer segment. */}
        {rows.map((row) => (
          <div
            key={row.status}
            onMouseEnter={() => setHovered(row.status)}
            onMouseLeave={() => setHovered(null)}
            onClick={onSelect ? () => onSelect(row.status) : undefined}
            className={`h-full transition-opacity duration-200 ${onSelect ? 'cursor-pointer' : ''}`}
            style={{
              width: `${(row.count / total) * 100}%`,
              background: row.color,
              opacity: hovered === null || hovered === row.status ? 1 : 0.45,
            }}
            title={`${row.label}: ${row.count}`}
          />
        ))}
      </div>

      {/* Legend doubling as the readout. Every segment is named and counted, so
          the bar never has to be decoded from colour alone. */}
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
        {rows.map((row) => (
          <li
            key={row.status}
            onMouseEnter={() => setHovered(row.status)}
            onMouseLeave={() => setHovered(null)}
            onClick={onSelect ? () => onSelect(row.status) : undefined}
            className={`flex items-center gap-2 text-2xs transition-opacity ${
              onSelect ? 'cursor-pointer hover:opacity-100' : ''
            } ${hovered === null || hovered === row.status ? 'opacity-100' : 'opacity-55'}`}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-sm ring-1 ring-black/5"
              style={{ background: row.color }}
            />
            <span className="truncate text-ink-secondary">{row.label}</span>
            <span className="ml-auto font-semibold tabular-nums text-ink">{number(row.count)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PipelineBar
